import { useEffect, useState } from 'react';
import api from '../../api/axios';
import S3ImagePicker from '../components/S3ImagePicker';
import RichEditor from '../components/RichEditor';

const EMPTY = {
  title: '',
  body: '',
  excerpt: '',
  category: 'Announcement',
  published_at: new Date().toISOString().substring(0, 10),
  published: true,
};
const CATEGORIES = ['Announcement', 'Event Report', 'Education', 'Community', 'Welfare', 'Other'];

export default function NewsManager() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [formKey, setFormKey] = useState('init');
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/news/admin')
      .then(r => setArticles(r.data.articles || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setImageFile(null);
    setImageUrl('');
    setError('');
    setFormKey('new-' + Date.now());
    setShowForm(true);
  };

  const openEdit = a => {
    setEditing(a.id);
    setForm({
      title: a.title,
      body: a.body || '',
      excerpt: a.excerpt || '',
      category: a.category || 'Announcement',
      published_at: a.published_at?.substring(0, 10) || '',
      published: a.published !== false,
    });
    setImageFile(null);
    setImageUrl(a.image_url || '');
    setError('');
    setFormKey(a.id);
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      if (imageFile) fd.append('image', imageFile);
      else if (imageUrl !== undefined) fd.append('image_url', imageUrl);
      if (editing) await api.put(`/news/${editing}`, fd);
      else await api.post('/news', fd);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await api.delete(`/news/${id}`);
    load();
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div>
      {showPicker && (
        <S3ImagePicker
          onSelect={url => { setImageUrl(url); setImageFile(null); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}

      <div className="d-flex align-items-center justify-content-between mb-4">
        <div><h4 className="fw-bold mb-0">News Articles</h4><p className="text-muted small mb-0">Manage news and announcements</p></div>
        <button className="btn btn-primary rounded-pill px-4" onClick={openAdd}><i className="fas fa-plus me-2"></i>Add Article</button>
      </div>

      {showForm && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">{editing ? 'Edit Article' : 'Add Article'}</h5>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <form onSubmit={save}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label small">Title *</label>
                  <input name="title" value={form.title} onChange={handle} required className="form-control" />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Category</label>
                  <select name="category" value={form.category} onChange={handle} className="form-select">
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Published Date</label>
                  <input name="published_at" type="date" value={form.published_at} onChange={handle} className="form-control" />
                </div>
                <div className="col-md-4 d-flex align-items-end pb-1">
                  <div className="form-check form-switch ms-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="newsPublished"
                      checked={form.published !== false && form.published !== 'false'}
                      onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}
                    />
                    <label className="form-check-label small fw-bold" htmlFor="newsPublished">Published (visible to public)</label>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label small">Cover Image</label>
                  <div className="d-flex gap-2 align-items-start">
                    <input type="file" accept="image/*" className="form-control" onChange={e => { setImageFile(e.target.files[0]); setImageUrl(''); }} />
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-3 text-nowrap" onClick={() => setShowPicker(true)}>
                      <i className="fas fa-folder-open me-1"></i>Media
                    </button>
                  </div>
                  {imageUrl && !imageFile && (
                    <div className="mt-2 d-flex align-items-center gap-2">
                      <img src={imageUrl} alt="preview" style={{ height: '48px', width: '64px', objectFit: 'cover', borderRadius: '4px' }} />
                      <button type="button" className="btn btn-sm btn-outline-danger rounded-pill px-2" onClick={() => setImageUrl('')}>Remove</button>
                    </div>
                  )}
                </div>
                <div className="col-12">
                  <label className="form-label small">Excerpt <span className="text-muted">(short summary shown on listing page)</span></label>
                  <input name="excerpt" value={form.excerpt} onChange={handle} className="form-control" placeholder="One or two sentences summarising the article…" />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-bold">Content *</label>
                  <RichEditor
                    key={formKey}
                    defaultValue={form.body}
                    onChange={html => setForm(f => ({ ...f, body: html }))}
                    placeholder="Write the full article content here…"
                    minHeight={280}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 mt-4">
                <button type="submit" disabled={saving} className="btn btn-primary rounded-pill px-4">{saving ? 'Saving…' : 'Save'}</button>
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr><th>Title</th><th>Category</th><th>Published</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {articles.length === 0 && <tr><td colSpan="5" className="text-center text-muted py-4">No articles yet.</td></tr>}
                {articles.map(a => (
                  <tr key={a.id}>
                    <td>{a.title}</td>
                    <td><span className="badge badge-soft-primary">{a.category}</span></td>
                    <td>{fmtDate(a.published_at)}</td>
                    <td>
                      {a.published !== false
                        ? <span className="badge badge-soft-success">Published</span>
                        : <span className="badge badge-soft-secondary">Draft</span>}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openEdit(a)}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(a.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
